"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { FileTextIcon } from "@radix-ui/react-icons";
import type { Question } from "@studygenie/db";
import { Loader2 } from "lucide-react";
import Image from "next/image";
import type { Dispatch, SetStateAction } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { IconYoutube } from "@/components/ui/icons";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/locales/client";
import { useEditQuestion } from "../api/use-edit-question";

type Props = {
  question: {
    questions: Question;
    fileName: string | null;
    sourceType: string | null;
  };
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};

const formSchema = z.object({
  question: z.string().min(1, { message: "Required" }),
  answer: z.string().optional().default(""),
});

export const EditQuestionForm = ({ question, setIsOpen }: Props) => {
  const t = useI18n();
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      question: question.questions.question,
      answer: question.questions.answer,
    },
  });

  const editQuestionMutation = useEditQuestion({
    id: question.questions.studySetId,
    questionId: question.questions.id,
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    editQuestionMutation.mutate(
      {
        id: question.questions.id,
        studySetId: question.questions.studySetId,
        question: values.question,
        answer: values.answer,
      },
      {
        onSuccess: () => setIsOpen(false),
      },
    );
  }

  const decideIcon = (fileName: string) => {
    if (fileName.endsWith(".pdf")) {
      return (
        <Image src="/text_line_pdf.png" height={20} width={17.5} alt="pdf" />
      );
    }
    if (fileName.endsWith(".pptx")) {
      return (
        <Image
          className="object-contain size-4"
          src="/PowerPoint.png"
          height={20.7}
          width={22.62}
          alt="powerpoint"
        />
      );
    }
    return (
      <Image
        className="object-contain size-4"
        src="/Word.png"
        height={20.7}
        width={22.62}
        alt="word"
      />
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="question"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("question")}</FormLabel>
              <FormControl>
                <Input
                  disabled={editQuestionMutation.isPending}
                  placeholder={t("questionActions.edit.questionPlaceholder")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="answer"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("answer")}</FormLabel>
              <FormControl>
                <Textarea
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      form.handleSubmit((data) => {
                        onSubmit(data);
                      })();
                    }
                  }}
                  minRows={3}
                  maxRows={5}
                  className="resize-none"
                  placeholder="Write the correct answer here..."
                  disabled={editQuestionMutation.isPending}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex gap-1 items-center">
          {question.fileName && (
            <>
              <h6 className="font-normal text-sm text-muted-foreground mr-[2px]">
                Source:{" "}
              </h6>
              {question.sourceType === "file" ? (
                <>
                  <span>{decideIcon(question.fileName)}</span>
                  <p className="font-normal text-xs max-w-fit break-all hyphens-auto">
                    {question.fileName}
                  </p>
                </>
              ) : question.sourceType === "plain_text" ? (
                <>
                  <FileTextIcon className="size-4" />
                  <p className="font-normal text-xs max-w-fit break-all hyphens-auto">
                    Pasted Text
                  </p>
                </>
              ) : question.sourceType === "youtube" ? (
                <>
                  <IconYoutube />
                  <p className="font-normal text-xs max-w-fit break-all hyphens-auto">
                    <a
                      className="hover:text-brand-primary hover:underline"
                      href={question.fileName}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {question.fileName}
                    </a>
                  </p>
                </>
              ) : null}
            </>
          )}
        </div>
        <div className="flex flex-col-reverse gap-y-4 md:gap-y-0 md:flex-row w-full md:items-center md:justify-between">
          <Button
            disabled={editQuestionMutation.isPending}
            onClick={() => setIsOpen(false)}
            variant="ghost"
            type="button"
          >
            {t("questionActions.edit.cancel")}
          </Button>
          <Button disabled={editQuestionMutation.isPending} type="submit">
            {editQuestionMutation.isPending ? (
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="size-4 animate-spin" />
                <p>{t("questionActions.edit.applyingEdits")}</p>
              </div>
            ) : (
              t("questionActions.edit.applyEdits")
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};
